/**
 * SEO Configuration
 * Central source of truth for all SEO-related constants
 * Uses dynamic domain configuration for localhost and production
 */

import { DYNAMIC_DOMAIN } from './dynamic-domain';

// Get domains dynamically - works on localhost and production
const getMainDomain = () => DYNAMIC_DOMAIN.getMainDomainUrl();
const getBlogSubdomainUrl = () => `${DYNAMIC_DOMAIN.getMainDomainUrl().replace(/\/$/, '')}/blogs`;

export const SEO_CONFIG = {
  // Personal Identity
  name: 'Sachin Sharma',
  role: 'Full Stack Developer & Mobile Engineer',
  gender: 'Male',
  location: {
    city: 'Delhi',
    neighborhood: 'Krishan Vihar',
    state: 'New Delhi',
    country: 'India',
    address: 'Krishan Vihar, Delhi, India',
    geo: {
      latitude: '28.7041',
      longitude: '77.1025',
    },
  },

  // Professional Identity
  specializations: [
    'Full Stack Developer',
    'Flutter Developer',
    'Mobile App Developer',
    'Cross-Platform Developer',
    'Android Developer',
    'Software Engineer',
    'Software developer',
    'Dart Developer',
    'Web Developer',
    'DevOps Engineer',
    'Game Developer',
    'Frontend Developer',
    'Backend Developer',
    'Cloud Engineer',
  ],

  // Core Skills (semantic relevance)
  skills: {
    mobile: ['Flutter', 'React Native', 'Dart', 'iOS', 'Android', 'Kotlin', 'Swift'],
    frontend: ['React', 'Next.js', 'TypeScript', 'JavaScript', 'Tailwind CSS', 'Vue.js', 'Angular', 'HTML5', 'CSS3'],
    backend: ['Node.js', 'Express.js', 'PostgreSQL', 'MongoDB', 'Supabase', 'Python', 'Django', 'FastAPI', 'REST API', 'GraphQL'],
    cloud: ['AWS', 'Docker', 'Firebase', 'Azure', 'GCP', 'Kubernetes', 'CI/CD', 'Jenkins', 'GitHub Actions'],
    gamedev: ['Unity', 'Unreal Engine', 'C#', 'C++', 'Game Design', '3D Graphics'],
    tools: ['Git', 'Linux', 'Nginx', 'Redis', 'Terraform', 'Ansible'],
  },

  // Social Proof & Authority
  social: {
    github: 'https://github.com/maisachinsharmahu',
    linkedin: 'https://www.linkedin.com/in/sachinsharmaofficial/',
    twitter: 'https://x.com/sachinsharmadev',
    instagram: 'https://www.instagram.com/sachinsharma.dev/',
  },

  // Experience
  experience: {
    years: '1.5+',
    companies: ['Self-Employed', 'ESPO', 'Kushal Aide'],
  },

  // Education
  education: {
    current: 'B.Tech in Computer Science and Technology',
    institution: 'Maharaja Agrasen Institute of Technology',
    diploma: 'Diploma in Computer Science and Engineering',
  },

  // Brand Identity
  tagline: 'Building Scalable Mobile Products that deliver Real Value',
  description: 'Sachin Sharma is a high-performance Full Stack Developer and Mobile Engineer based in Delhi, India. Specializing in cross-platform mobile development with Flutter, cloud services, and intuitive system architecture. With over 1.5 years of professional experience, Sachin serves global clients with production-ready applications and scalable engineering solutions.',

  // Contact
  email: 'sachinsharmaaofficial@gmail.com',

  // Blog Brand
  blogName: "Sachin's DevLog",

  // Get domain dynamically (localhost:3000 or production)
  get domain() {
    return getMainDomain();
  },

  // Get blog subdomain dynamically
  get blogDomain() {
    return getBlogSubdomainUrl();
  },

  // Default OG Image
  get ogImage() {
    return `${this.domain}/api/og`;
  },

  // Profile Image
  avatar: "/sachinsharma.webp",
} as const;

/**
 * Generate person schema for structured data
 */
export function generatePersonSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${SEO_CONFIG.domain}/#person`,
    name: SEO_CONFIG.name,
    url: SEO_CONFIG.domain,
    image: SEO_CONFIG.avatar,
    jobTitle: SEO_CONFIG.role,
    gender: SEO_CONFIG.gender,
    description: SEO_CONFIG.description,
    disambiguatingDescription:
      'Software developer and mobile engineer based in Delhi, India — distinct from other public figures named Sachin Sharma (musician, academic, or corporate executives of the same name).',
    address: {
      '@type': 'PostalAddress',
      addressLocality: SEO_CONFIG.location.city,
      addressRegion: SEO_CONFIG.location.state,
      addressCountry: SEO_CONFIG.location.country,
      streetAddress: SEO_CONFIG.location.neighborhood,
    },
    hasCredential: [
      {
        '@type': 'EducationalOccupationalCredential',
        name: SEO_CONFIG.education.current,
        credentialCategory: 'degree',
        recognizedBy: {
          '@type': 'EducationalOrganization',
          name: SEO_CONFIG.education.institution,
        },
      },
      {
        '@type': 'EducationalOccupationalCredential',
        name: SEO_CONFIG.education.diploma,
        credentialCategory: 'diploma',
      },
    ],
    alumniOf: {
      '@type': 'EducationalOrganization',
      name: SEO_CONFIG.education.institution,
      url: 'https://mait.ac.in/',
    },
    sameAs: [
      SEO_CONFIG.social.github,
      SEO_CONFIG.social.linkedin,
      SEO_CONFIG.social.twitter,
      SEO_CONFIG.social.instagram,
    ],
    knowsAbout: [
      {
        '@type': 'Thing',
        name: 'Flutter Development',
        sameAs: 'https://en.wikipedia.org/wiki/Flutter_(software)',
      },
      {
        '@type': 'Thing',
        name: 'Mobile App Development',
        sameAs: 'https://en.wikipedia.org/wiki/Mobile_app_development',
      },
      {
        '@type': 'Thing',
        name: 'React (JavaScript library)',
        sameAs: 'https://en.wikipedia.org/wiki/React_(software)',
      },
      {
        '@type': 'Thing',
        name: 'Next.js',
        sameAs: 'https://en.wikipedia.org/wiki/Next.js',
      },
      ...SEO_CONFIG.skills.mobile,
      ...SEO_CONFIG.skills.frontend,
      ...SEO_CONFIG.skills.backend,
      ...SEO_CONFIG.skills.cloud,
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'technical support',
      email: SEO_CONFIG.email,
      url: `${SEO_CONFIG.domain}/#contact`,
    },
  };
}

/**
 * Generate website schema for structured data
 */
export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: `${SEO_CONFIG.name} - Developer Portfolio`,
    url: SEO_CONFIG.domain,
    description: SEO_CONFIG.description,
    author: {
      '@type': 'Person',
      name: SEO_CONFIG.name,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SEO_CONFIG.blogDomain}?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Generate profile page schema
 */
export function generateProfilePageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    dateCreated: '2024-01-01T00:00:00+00:00',
    dateModified: new Date().toISOString(),
    mainEntity: generatePersonSchema(),
  };
}

/**
 * Generate breadcrumb schema
 */
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Generate article schema for blog posts
 */
export function generateArticleSchema(article: {
  title: string;
  description: string;
  publishedDate: string;
  modifiedDate: string;
  image: string;
  url: string;
  keywords?: string[];
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: article.publishedDate,
    dateModified: article.modifiedDate,
    author: generatePersonSchema(),
    publisher: {
      '@type': 'Person',
      name: SEO_CONFIG.name,
      logo: {
        '@type': 'ImageObject',
        url: SEO_CONFIG.avatar,
      },
    },
    url: article.url,
    keywords: article.keywords?.join(', '),
  };
}

/**
 * Generate FAQ schema for homepage
 */
export function generateFAQSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What services does Sachin Sharma offer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'I offer custom software development, mobile app development (Flutter/React Native), web development (React/Next.js), cloud infrastructure (AWS/Docker), and full-stack engineering solutions.',
        },
      },
      {
        '@type': 'Question',
        name: 'What technologies does Sachin Sharma specialize in?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'I specialize in Flutter, React Native, React, Next.js, TypeScript, Node.js, AWS, Docker, and more. I have expertise across mobile, frontend, backend, and DevOps domains.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many years of experience does Sachin Sharma have?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${SEO_CONFIG.experience.years} years of professional experience building production-grade applications and leading engineering initiatives.`,
        },
      },
      {
        '@type': 'Question',
        name: 'Where is Sachin Sharma based?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${SEO_CONFIG.name} is based in ${SEO_CONFIG.location.neighborhood}, ${SEO_CONFIG.location.city}, ${SEO_CONFIG.location.country}. Available for remote work globally.`,
        },
      },
      {
        '@type': 'Question',
        name: 'Who is Sachin Sharma the developer?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sachin Sharma is a Software Developer and Mobile Engineer based in Delhi, India, currently freelancing full-stack for a ₹150 Cr+ revenue business conglomerate after previously building production Flutter apps at ESPO. He holds a B.Tech in Computer Science and Technology from MAIT and has been a hackathon finalist three times. Not to be confused with other public figures sharing the same name (musicians, actors, civil servants, or executives).',
        },
      },
      {
        '@type': 'Question',
        name: 'What has Sachin Sharma built?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sachin Sharma has built Loopin (a privacy-first RPG habit tracker), Legease (an AI-assisted legal document analyzer), and Aashwasan (an offline-first mental wellness app) — all real, shipped Flutter and React codebases.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is Sachin Sharma\'s educational background?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sachin Sharma holds a B.Tech in Computer Science and Technology from Maharaja Agrasen Institute of Technology (MAIT), CGPA 9.08, entered via lateral entry after a Diploma in Computer Science and Engineering from Ambedkar DSEU, Shakarpur (CGPA 9.22).',
        },
      },
      {
        '@type': 'Question',
        name: 'Is Sachin Sharma available for hire?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes. Sachin Sharma is available for freelance, contract, and full-time opportunities in Flutter, React, Next.js, and full-stack development. Contact is direct, with no agency or recruiter in between.',
        },
      },
    ],
  };
}

/**
 * Generate Organization schema
 */
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SEO_CONFIG.domain}#organization`,
    name: SEO_CONFIG.name,
    url: SEO_CONFIG.domain,
    logo: {
      '@type': 'ImageObject',
      url: `${SEO_CONFIG.domain}/favicon.png`,
    },
    description: SEO_CONFIG.description,
    email: SEO_CONFIG.email,
    member: {
      '@id': `${SEO_CONFIG.domain}/#person`,
    },
    location: {
      '@type': 'PostalAddress',
      addressLocality: SEO_CONFIG.location.city,
      addressRegion: SEO_CONFIG.location.state,
      addressCountry: SEO_CONFIG.location.country,
    },
    sameAs: [
      SEO_CONFIG.social.github,
      SEO_CONFIG.social.linkedin,
      SEO_CONFIG.social.twitter,
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Contact',
      email: SEO_CONFIG.email,
      availableLanguage: ['en'],
    },
  };
}

/**
 * Generate Search Box schema
 */
export function generateSearchBoxSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: SEO_CONFIG.domain,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SEO_CONFIG.blogDomain}?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Generate SoftwareApplication schema for projects
 */
export function generateSoftwareApplicationSchema(app: {
  name: string;
  description: string;
  image: string;
  url: string;
  category: string;
  operatingSystems: string[];
  applicationCategory: 'BusinessApplication' | 'UtilityApplication' | 'GameApplication' | 'SocialNetworkingApplication';
  version?: string;
  datePublished?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: app.name,
    description: app.description,
    image: app.image,
    url: app.url,
    author: generatePersonSchema(),
    applicationCategory: app.applicationCategory,
    operatingSystem: app.operatingSystems,
    ...(app.version && { softwareVersion: app.version }),
    ...(app.datePublished && { datePublished: app.datePublished }),
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  };
}

/**
 * Generate HowTo schema for blog tutorials
 */
export function generateHowToSchema(howto: {
  title: string;
  description: string;
  image: string;
  steps: Array<{
    name: string;
    description: string;
    image?: string;
  }>;
  totalTime: string;
  yield: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: howto.title,
    description: howto.description,
    image: howto.image,
    totalTime: howto.totalTime,
    yield: howto.yield,
    step: howto.steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.description,
      ...(step.image && {
        image: step.image,
      }),
    })),
  };
}

/**
 * Generate SEO-optimized metadata base
 */
export function generateMetadata({
  title,
  description,
  path = '',
  keywords = [],
  image,
  type = 'website',
}: {
  title: string;
  description: string;
  path?: string;
  keywords?: string[];
  image?: string;
  type?: 'website' | 'article' | 'profile';
}) {
  const url = `${SEO_CONFIG.domain}${path}`;
  const ogImage = image || SEO_CONFIG.ogImage;

  // Core identity keywords (for ranking)
  const coreKeywords = [
    SEO_CONFIG.name,
    'sachinsharma',
    `${SEO_CONFIG.name} developer`,
    `${SEO_CONFIG.name} ${SEO_CONFIG.location.city}`,
    `${SEO_CONFIG.name} India`,
  ];

  return {
    title,
    description,
    keywords: [...coreKeywords, ...keywords].join(', '),
    authors: [{ name: SEO_CONFIG.name, url: SEO_CONFIG.domain }],
    creator: SEO_CONFIG.name,
    publisher: SEO_CONFIG.name,
    metadataBase: new URL(SEO_CONFIG.domain),
    alternates: {
      canonical: url,
    },
    icons: {
      icon: [
        { url: '/favicon.ico', sizes: 'any' }, // Standard fallback
        { url: '/favicon.png', sizes: '576x576', type: 'image/png' },
      ],
      apple: [
        { url: '/apple-touch-icon.png', sizes: '180x180', type: 'image/png' },
      ],
    },
    openGraph: {
      type,
      locale: 'en_IN',
      url,
      siteName: `${SEO_CONFIG.name} - Portfolio`,
      title,
      description,
      images: [
        {
          url: ogImage,
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
    },
    twitter: {
      card: 'summary_large_image',
      site: '@sachinsharmadev',
      creator: '@sachinsharmadev',
      title,
      description,
      images: [ogImage],
    },
    // Additional metadata for better social sharing (WhatsApp, Telegram, etc.)
    other: {
      'og:image:width': '1200',
      'og:image:height': '630',
      'og:image:alt': title,
      'og:image:type': 'image/png',
    },
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        'max-video-preview': -1,
        'max-image-preview': 'large' as const,
        'max-snippet': -1,
      },
    },
  };
}
